import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { Item, Step } from 'modules/processes/domain'
import { useBulkUpdateTaskConfigurationFiles } from 'modules/processes/hooks/useBulkUpdateTaskConfigurationFiles'
import { useCreateItem } from 'modules/processes/hooks/useCreateItem'
import { useUpdateItem } from 'modules/processes/hooks/useUpdateItem'
import { useProcessContext } from 'modules/processes/pages/Process/ProcessRootPage'

import type ItemForm from '../components/Form/ItemForm'

export const useMutateItem = ({
  item,
  onClose,
  form,
  dueDateOpen,
}: {
  item?: Item
  onClose: () => void
  form: ItemForm
  currentStep: Step
  dueDateOpen: boolean
}) => {
  const { process } = useProcessContext()
  const { createItem } = useCreateItem()
  const { updateItem } = useUpdateItem()
  const updateFiles = useBulkUpdateTaskConfigurationFiles()

  const upsertItem = async (id: number | undefined) => {
    const {
      title,
      mandatory,
      instruction,
      data_collection_configuration,
      access_ids,
      assignee_ids,
      assignee_strategy,
      offset_amount,
      offset_direction,
      reference_field_key,
      step_id,
      position,
      additional_form_data,
      task_type_configuration,
      task_type,
    } = form.values

    const additionalFormData = additional_form_data[task_type]

    const newData = {
      title,
      mandatory,
      instruction,
      stepId: +step_id,
      position: +position,
      taskType: task_type,
      dataCollectionConfiguration: data_collection_configuration || {},
      accessIds: access_ids,
      assigneeIds: assignee_ids,
      assigneeStrategy: assignee_strategy,
      offsetAmount: dueDateOpen ? Number(offset_amount) : null,
      offsetDirection: dueDateOpen ? String(offset_direction) : null,
      referenceFieldKey: dueDateOpen ? String(reference_field_key) : null,
      ...(task_type_configuration && {
        taskTypeConfiguration: JSON.stringify(task_type_configuration),
      }),
      ...(additionalFormData && additionalFormData),
    }

    if (id) {
      const itemUpdated = await updateItem(
        {
          id: +id,
          ...newData,
        },
        process.id
      )

      track(trackingEvents.processes.processTaskSaved, {
        isWizard: true,
      })
      return itemUpdated
    }

    const res = await createItem(newData, process.id)

    track(trackingEvents.processes.processTaskCreated, {
      category: process.category,
      isMandatory: form.get('mandatory').value,
      isWizard: true,
    })

    return res
  }

  const onSubmit = async () => {
    const newItem = await upsertItem(item?.id)

    const { files } = form.values

    const filesChanges = files?.filesToAdd?.length || files?.filesToRemove?.size
    const taskConfigurationId = newItem?.configurationId

    const shouldUpdateFiles = taskConfigurationId && filesChanges

    if (!shouldUpdateFiles) return onClose()

    await updateFiles(
      {
        taskConfigurationId,
        filesToAdd: files?.filesToAdd,
        filesToRemove: [...files?.filesToRemove],
      },
      taskConfigurationId
    )

    onClose()
  }

  return {
    onSubmit,
  }
}
